/**
 * The Leaflet map of Florida under the prism overlay. Leaflet is used directly
 * (as in FacilityHeatMap and BdMapView): OpenStreetMap tiles, Florida centre,
 * the wheel off until the map is clicked so a scrolling page is never hijacked.
 * The container keeps Leaflet's own keyboard behaviour (arrows pan, +/- zoom)
 * and Tab leaves it as usual - it is not a trap. The ranked list is the
 * keyboard path to each facility.
 */
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PrismLayer, { type PrismLayerProps } from './PrismLayer';

const FLORIDA_CENTER: L.LatLngExpression = [28.5, -81.5];
const FLORIDA_ZOOM = 6.3;

export default function AtlasMap({ message, ...layer }: Omit<PrismLayerProps, 'map'> & { message?: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const m = L.map(containerRef.current, {
      center: FLORIDA_CENTER,
      zoom: FLORIDA_ZOOM,
      zoomSnap: 0.1,
      scrollWheelZoom: false, // a map inside a scrolling page must not hijack the wheel
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
    }).addTo(m);
    m.on('click', () => m.scrollWheelZoom.enable());
    m.on('mouseout', () => m.scrollWheelZoom.disable());
    setMap(m);
    return () => { m.remove(); setMap(null); };
  }, []);

  return (
    <div className="atlas-map" role="region" aria-label="Map of Florida, one column per facility. The ranked list below has the same figures.">
      <div ref={containerRef} className="atlas-map__leaflet" data-testid="atlas-leaflet" />
      <PrismLayer map={map} {...layer} />
      {message && <div className="atlas-map__msg" role="status">{message}</div>}
    </div>
  );
}
